import { useState, useCallback, useRef, useEffect } from "react";
import chatService from "@plannel/services/utils/chat-service";
import authService from "@plannel/services/system/auth-service";

/**
 * AI 채팅 훅
 *
 * @param {string} pageId  - 현재 화면 viewName (예: "reviewBaseline")
 *
 * 반환값:
 *   messages   - { role: "user"|"assistant", content: string }[]
 *   loading    - 응답 대기 중 여부
 *   error      - 오류 메시지 (없으면 null)
 *   sendMessage(prompt) - 메시지 전송
 *   reset()    - 대화 초기화
 *
 * [메모리 연속성]
 * AgentCore 메모리는 (tenant_id, user_id, session_id) 조합으로 식별됩니다.
 * 세션 ID를 localStorage에 저장하여 페이지 새로고침 / 재방문 후에도 같은
 * 세션으로 대화가 이어지도록 합니다.
 */

/** localStorage 키 생성 — tenant+user+page 조합으로 격리 */
const makeStorageKey = (pageId) => {
  const user = authService.getCurrentUser();
  const tenant = user?.tenantId ?? "unknown";
  const userId = user?.username ?? "unknown";
  return `plannel_chat_session_${tenant}_${userId}_${pageId ?? "global"}`;
};

const useChat = (pageId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  // localStorage에서 세션 ID 복원 (없으면 undefined → 서버가 생성)
  const sessionIdRef = useRef(undefined);
  useEffect(() => {
    const stored = localStorage.getItem(makeStorageKey(pageId));
    if (stored) {
      sessionIdRef.current = stored;
    }
  }, [pageId]);

  const sendMessage = useCallback(async (prompt, pageContext, isAutoAnalysis = false) => {
    if (!prompt || !prompt.trim()) return;

    setError(null);
    setMessages(prev => [...prev, { role: "user", content: prompt.trim(), hidden: isAutoAnalysis }]);
    setLoading(true);

    const mode = isAutoAnalysis ? "analyze" : "chat";

    try {
      const res = await chatService.sendMessage(prompt.trim(), pageId, sessionIdRef.current, pageContext, mode);
      const data = res.data;

      // 멀티턴: 서버가 반환한 sessionId를 ref + localStorage에 저장
      if (data.sessionId) {
        sessionIdRef.current = data.sessionId;
        try {
          localStorage.setItem(makeStorageKey(pageId), data.sessionId);
        } catch (_) { /* localStorage 쓰기 실패 무시 */ }
      }

      setMessages(prev => [...prev, { role: "assistant", content: data.answer }]);
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.message ?? "알 수 없는 오류";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  const reset = useCallback(() => {
    setMessages([]);
    sessionIdRef.current = undefined;
    try {
      localStorage.removeItem(makeStorageKey(pageId));
    } catch (_) { /* 무시 */ }
    setError(null);
  }, [pageId]);

  return { messages, loading, error, sendMessage, reset };
};

export default useChat;



